import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ApiError } from '@/lib/api-client'
import { fromDateTimeLocalInput, OPERATIONS_TIME_ZONE_LABEL, toDateTimeLocalInput } from '@/lib/datetime'
import { queryKeys } from '@/lib/query-client'
import type { AdminPromo } from '@/types/api'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Field } from '@/components/ui/Field'
import { Input, Select, Textarea } from '@/components/ui/Input'
import { useToast } from '@/components/ui/useToast'
import { createPromoCode, updatePromoCode } from './api'
import styles from './PromoFormDialog.module.css'

/**
 * The rules the backend enforces, restated in the browser so an operator finds
 * out about a bad date range while typing rather than after submitting.
 */
const schema = z
  .object({
    code: z
      .string()
      .trim()
      .min(2, 'A code needs at least 2 characters')
      .max(50, 'A code can be at most 50 characters')
      .regex(/^[A-Za-z0-9_-]+$/, 'Use letters, digits, dashes and underscores only'),
    description: z.string().trim().max(255, 'Keep the description under 255 characters'),
    discountType: z.enum(['percentage', 'fixed']),
    discountValue: z.coerce.number().positive('Enter a value greater than zero'),
    maxDiscountValue: z.coerce.number().min(0, 'A cap cannot be negative'),
    usageLimitTotal: z.coerce.number().int('Use a whole number').min(0, 'Use 0 for no overall limit'),
    usageLimitPerUser: z.coerce.number().int('Use a whole number').min(1, 'Each customer needs at least one use'),
    startDate: z.string().min(1, 'Choose when the code becomes valid'),
    endDate: z.string().min(1, 'Choose when the code stops working'),
    isActive: z.boolean(),
  })
  .superRefine((values, context) => {
    if (values.discountType === 'percentage') {
      if (values.discountValue > 100) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['discountValue'],
          message: 'A percentage discount cannot exceed 100%',
        })
      }
      if (values.maxDiscountValue <= 0) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['maxDiscountValue'],
          // Without a cap, a percentage off an expensive trip is unbounded.
          message: 'A percentage discount needs a maximum amount',
        })
      }
    }

    if (values.usageLimitTotal > 0 && values.usageLimitPerUser > values.usageLimitTotal) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['usageLimitPerUser'],
        message: 'Cannot exceed the total usage limit',
      })
    }

    if (values.startDate && values.endDate && values.endDate <= values.startDate) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDate'],
        message: 'The end must be after the start',
      })
    }
  })

type PromoValues = z.infer<typeof schema>

interface PromoFormDialogProps {
  open: boolean
  /** `null` creates; a promo edits it. */
  promo: AdminPromo | null
  onClose: () => void
}

const EMPTY: PromoValues = {
  code: '',
  description: '',
  discountType: 'percentage',
  discountValue: 10,
  maxDiscountValue: 100,
  usageLimitTotal: 0,
  usageLimitPerUser: 1,
  startDate: '',
  endDate: '',
  isActive: true,
}

export function PromoFormDialog({ open, promo, onClose }: PromoFormDialogProps) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const isEdit = promo !== null

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<PromoValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })

  useEffect(() => {
    if (!open) return

    reset(
      promo
        ? {
            code: promo.code,
            description: promo.description ?? '',
            discountType: promo.discountType?.toUpperCase() === 'FIXED' ? 'fixed' : 'percentage',
            discountValue: promo.discountValue ?? 0,
            maxDiscountValue: promo.maxDiscountAmount ?? 0,
            usageLimitTotal: promo.usageLimitTotal ?? 0,
            usageLimitPerUser: promo.usageLimitPerUser ?? 1,
            startDate: toDateTimeLocalInput(promo.startDate),
            endDate: toDateTimeLocalInput(promo.endDate),
            isActive: promo.isActive,
          }
        : EMPTY,
    )
  }, [open, promo, reset])

  const discountType = watch('discountType')

  const onSubmit = async (values: PromoValues) => {
    const payload = {
      code: values.code.trim().toUpperCase(),
      description: values.description.trim(),
      discountType: values.discountType,
      discountValue: Math.round(values.discountValue),
      maxDiscountValue: Math.round(values.maxDiscountValue),
      usageLimitTotal: Math.round(values.usageLimitTotal),
      usageLimitPerUser: Math.round(values.usageLimitPerUser),
      startDate: fromDateTimeLocalInput(values.startDate),
      endDate: fromDateTimeLocalInput(values.endDate),
    }

    try {
      if (promo) {
        await updatePromoCode({ ...payload, id: promo.id, isActive: values.isActive })
        toast.success('Promo code updated', `${payload.code} has been saved.`)
      } else {
        await createPromoCode(payload)
        toast.success('Promo code created', `${payload.code} is ready to use.`)
      }

      void queryClient.invalidateQueries({ queryKey: queryKeys.promos.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all })
      onClose()
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.fieldErrors) {
          for (const [field, message] of Object.entries(error.fieldErrors)) {
            if (field in EMPTY) {
              setError(field as keyof PromoValues, { message })
            }
          }
        } else if (/code/i.test(error.message) && /exist/i.test(error.message)) {
          setError('code', { message: error.message })
        }
        toast.error(promo ? 'Could not save the promo code' : 'Could not create the promo code', error.message)
        return
      }
      toast.error('Something went wrong', 'The promo code could not be saved.')
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEdit ? `Edit ${promo.code}` : 'New promo code'}
      description={
        isEdit
          ? 'Changes take effect immediately for anyone entering this code.'
          : 'Create a discount customers can apply at checkout in the app.'
      }
      size="lg"
      busy={isSubmitting}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="promo-form" loading={isSubmitting}>
            {isEdit ? 'Save changes' : 'Create promo code'}
          </Button>
        </>
      }
    >
      <form id="promo-form" className={styles.form} onSubmit={(event) => void handleSubmit(onSubmit)(event)} noValidate>
        <div className={styles.grid}>
          <Field label="Code" error={errors.code?.message} hint="Stored in upper case." required>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                placeholder="SUMMER25"
                autoCapitalize="characters"
                spellCheck={false}
                disabled={isSubmitting}
                {...register('code')}
              />
            )}
          </Field>

          <Field label="Discount type" error={errors.discountType?.message} required>
            {({ id, describedBy, invalid }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                disabled={isSubmitting}
                {...register('discountType')}
              >
                <option value="percentage">Percentage off</option>
                <option value="fixed">Fixed amount off</option>
              </Select>
            )}
          </Field>

          <Field
            label={discountType === 'percentage' ? 'Percentage off' : 'Amount off (EGP)'}
            error={errors.discountValue?.message}
            required
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                type="number"
                min={1}
                max={discountType === 'percentage' ? 100 : undefined}
                step={1}
                disabled={isSubmitting}
                {...register('discountValue')}
              />
            )}
          </Field>

          <Field
            label="Maximum discount (EGP)"
            error={errors.maxDiscountValue?.message}
            hint={
              discountType === 'percentage'
                ? 'Caps how much a percentage discount can take off one trip.'
                : 'Not used for a fixed discount — leave at 0.'
            }
            required={discountType === 'percentage'}
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                type="number"
                min={0}
                step={1}
                disabled={isSubmitting}
                {...register('maxDiscountValue')}
              />
            )}
          </Field>

          <Field
            label="Total uses"
            error={errors.usageLimitTotal?.message}
            hint="0 means unlimited."
            required
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                type="number"
                min={0}
                step={1}
                disabled={isSubmitting}
                {...register('usageLimitTotal')}
              />
            )}
          </Field>

          <Field label="Uses per customer" error={errors.usageLimitPerUser?.message} required>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                type="number"
                min={1}
                step={1}
                disabled={isSubmitting}
                {...register('usageLimitPerUser')}
              />
            )}
          </Field>

          <Field
            label="Valid from"
            error={errors.startDate?.message}
            hint={`${OPERATIONS_TIME_ZONE_LABEL} time`}
            required
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                type="datetime-local"
                disabled={isSubmitting}
                {...register('startDate')}
              />
            )}
          </Field>

          <Field
            label="Valid until"
            error={errors.endDate?.message}
            hint={`${OPERATIONS_TIME_ZONE_LABEL} time`}
            required
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                type="datetime-local"
                disabled={isSubmitting}
                {...register('endDate')}
              />
            )}
          </Field>
        </div>

        <Field label="Description" error={errors.description?.message}>
          {({ id, describedBy, invalid }) => (
            <Textarea
              id={id}
              aria-describedby={describedBy}
              invalid={invalid}
              rows={2}
              placeholder="Summer campaign — 25% off city trips"
              disabled={isSubmitting}
              {...register('description')}
            />
          )}
        </Field>

        {isEdit ? (
          <Controller
            control={control}
            name="isActive"
            render={({ field }) => (
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={field.value}
                  onChange={(event) => field.onChange(event.target.checked)}
                  disabled={isSubmitting}
                />
                <span className={styles.toggleText}>
                  <span className={styles.toggleTitle}>Code is enabled</span>
                  <span className={styles.toggleHint}>
                    Turning this off disables the code immediately, whatever its dates say.
                  </span>
                </span>
              </label>
            )}
          />
        ) : null}
      </form>
    </Dialog>
  )
}
